/**
 * buddyLayerMap — selects body, eyes, and mouth layers independently
 * based on ComposedBuddyState + backend scores.
 */

import { ComposedBuddyState } from '@/lib/state/baselineMoodComposer';
import { TransientReactionType } from '@/lib/types/events';
import { SignatureBehavior } from '@/lib/state/signatureBehaviorMap';

// ── Body layer imports ──
import bodyNeutral from '@/assets/buddy/layers/body-neutral.webp';
import bodySlouch from '@/assets/buddy/layers/body-slouch.webp';
import bodyUpright from '@/assets/buddy/layers/body-upright.webp';
import bodyLeanForward from '@/assets/buddy/layers/body-lean-forward.webp';

// ── Eyes layer imports ──
import eyesOpen from '@/assets/buddy/layers/eyes-open.webp';
import eyesHalf from '@/assets/buddy/layers/eyes-half.webp';
import eyesWide from '@/assets/buddy/layers/eyes-wide.webp';
import eyesAngry from '@/assets/buddy/layers/eyes-angry.webp';
import eyesHappy from '@/assets/buddy/layers/eyes-happy.webp';
import eyesClosed from '@/assets/buddy/layers/eyes-closed.webp';
import eyesSide from '@/assets/buddy/layers/eyes-side.webp';

// ── Mouth layer imports ──
import mouthSmile from '@/assets/buddy/layers/mouth-smile.webp';
import mouthGrin from '@/assets/buddy/layers/mouth-grin.webp';
import mouthNeutral from '@/assets/buddy/layers/mouth-neutral.webp';
import mouthFrown from '@/assets/buddy/layers/mouth-frown.webp';
import mouthOpen from '@/assets/buddy/layers/mouth-open.webp';
import mouthAngry from '@/assets/buddy/layers/mouth-angry.webp';
import mouthSmirk from '@/assets/buddy/layers/mouth-smirk.webp';

export type BodyKey = 'neutral' | 'slouch' | 'upright' | 'leanForward';
export type EyesKey = 'open' | 'half' | 'wide' | 'angry' | 'happy' | 'closed' | 'side';
export type MouthKey = 'smile' | 'grin' | 'neutral' | 'frown' | 'open' | 'angry' | 'smirk';

export const BODY_ASSETS: Record<BodyKey, string> = {
  neutral: bodyNeutral,
  slouch: bodySlouch,
  upright: bodyUpright,
  leanForward: bodyLeanForward,
};

export const EYES_ASSETS: Record<EyesKey, string> = {
  open: eyesOpen,
  half: eyesHalf,
  wide: eyesWide,
  angry: eyesAngry,
  happy: eyesHappy,
  closed: eyesClosed,
  side: eyesSide,
};

export const MOUTH_ASSETS: Record<MouthKey, string> = {
  smile: mouthSmile,
  grin: mouthGrin,
  neutral: mouthNeutral,
  frown: mouthFrown,
  open: mouthOpen,
  angry: mouthAngry,
  smirk: mouthSmirk,
};

// All assets for preloading
export const ALL_LAYER_ASSETS = [
  ...Object.values(BODY_ASSETS),
  ...Object.values(EYES_ASSETS),
  ...Object.values(MOUTH_ASSETS),
];

export interface BuddyLayers {
  body: string;
  bodyKey: BodyKey;
  eyes: string;
  eyesKey: EyesKey;
  mouth: string;
  mouthKey: MouthKey;
}

// ── Body selection ──
function selectBody(
  composed: ComposedBuddyState,
  buddyMood?: string,
): BodyKey {
  const { smileBias } = composed.baseline;
  const mood = composed.shortTermMood;

  if (smileBias > 0.3 || mood === 'hyped' || mood === 'celebrating') return 'upright';
  if (smileBias < -0.3 || mood === 'shaken' || mood === 'stressed') return 'slouch';
  if (mood === 'curious' || buddyMood === 'curious') return 'leanForward';

  return 'neutral';
}

// ── Eyes selection ──
function selectEyes(
  composed: ComposedBuddyState,
  activeSignature?: SignatureBehavior | null,
): EyesKey {
  const { activeTransient } = composed;
  const { eyeTension, smileBias } = composed.baseline;

  // Transient priority
  if (activeTransient) {
    const t = activeTransient;
    if (t === 'halvingHype' || t === 'greedPeak') return 'happy';
    if (t === 'fearSurge' || t === 'longWait') return 'wide';
    if (t === 'feeSpike') return 'angry';
    if (t === 'blockFound' || t === 'backToBack' || t === 'pricePump') return 'happy';
    if (t === 'priceDip') return 'wide';
    if (t === 'volatilityShock') return 'wide';
    if (t === 'droughtAnxiety') return 'half';
    if (t === 'feeRelief') return 'happy';
  }

  // Signature priority
  if (activeSignature) {
    if (activeSignature === 'queueStare') return 'side';
    if (activeSignature === 'crabDeadpan') return 'half';
    if (activeSignature === 'laserFlex') return 'happy';
    if (activeSignature === 'reliefExhale') return 'happy';
    if (activeSignature === 'feeFeverTwitch') return 'angry';
    if (activeSignature === 'minerPrayer') return 'closed';
  }

  // Baseline
  if (eyeTension > 0.7) return 'angry';
  if (eyeTension > 0.4) return 'wide';
  // Only use happy eyes for truly hyped/celebrating moods, not calm states
  if (smileBias > 0.4 && (composed.shortTermMood === 'hyped' || composed.shortTermMood === 'celebrating')) return 'happy';

  // Mood fallback
  const mood = composed.shortTermMood;
  if (mood === 'celebrating' || mood === 'hyped') return 'happy';
  if (mood === 'stressed') return 'angry';
  if (mood === 'shaken') return 'wide';
  if (mood === 'calm' || mood === 'vibing' || mood === 'relieved') return 'open';

  return 'open';
}

// ── Mouth selection ──
function selectMouth(
  composed: ComposedBuddyState,
  activeSignature?: SignatureBehavior | null,
): MouthKey {
  const { activeTransient } = composed;
  const { smileBias, eyeTension } = composed.baseline;

  // Transient priority
  if (activeTransient) {
    const t = activeTransient;
    if (t === 'blockFound' || t === 'pricePump' || t === 'backToBack') return 'grin';
    if (t === 'fearSurge' || t === 'priceDip') return 'frown';
    if (t === 'feeSpike') return 'angry';
    if (t === 'halvingHype' || t === 'greedPeak') return 'open';
    if (t === 'longWait' || t === 'droughtAnxiety') return 'frown';
    if (t === 'volatilityShock') return 'open';
    if (t === 'feeRelief') return 'smile';
  }

  // Signature
  if (activeSignature) {
    if (activeSignature === 'reliefExhale' || activeSignature === 'laserFlex') return 'grin';
    if (activeSignature === 'feeFeverTwitch' || activeSignature === 'queueStare') return 'angry';
    if (activeSignature === 'minerPrayer') return 'neutral';
    if (activeSignature === 'crabDeadpan') return 'neutral';
  }

  // Baseline — only grin for truly excited moods
  if (smileBias > 0.4 && (composed.shortTermMood === 'hyped' || composed.shortTermMood === 'celebrating')) return 'grin';
  if (smileBias > 0.3) return 'smile';
  if (smileBias > 0.05) return 'smirk';
  if (smileBias < -0.3) return 'frown';
  if (smileBias < -0.1) return 'neutral';
  if (eyeTension > 0.7) return 'angry';

  // Mood fallback
  const mood = composed.shortTermMood;
  if (mood === 'celebrating' || mood === 'hyped') return 'grin';
  if (mood === 'stressed') return 'angry';
  if (mood === 'shaken') return 'frown';
  if (mood === 'calm' || mood === 'vibing') return 'smile';

  return 'neutral';
}

/**
 * Select all three layers based on composed state.
 */
export function selectBuddyLayers(
  composed: ComposedBuddyState,
  activeSignature?: SignatureBehavior | null,
  buddyMood?: string,
): BuddyLayers {
  const bodyKey = selectBody(composed, buddyMood);
  const eyesKey = selectEyes(composed, activeSignature);
  const mouthKey = selectMouth(composed, activeSignature);

  return {
    body: BODY_ASSETS[bodyKey],
    bodyKey,
    eyes: EYES_ASSETS[eyesKey],
    eyesKey,
    mouth: MOUTH_ASSETS[mouthKey],
    mouthKey,
  };
}
